import { TreeNodeData, NodeMetrics } from '../types';

/**
 * Helper to generate node key using the same logic as treeTransformers
 */
const generateNodeKey = (name: string, parentKey: string = ''): string => {
  const encodedName = encodeURIComponent(name);
  return parentKey ? `${parentKey}:${encodedName}` : encodedName;
};

/**
 * Interface to hold node information during traversal
 */
interface NodeInfo {
  node: TreeNodeData;
  key: string;
  parentValue: number | null;
  parentContribution: number; // The pct_of_root of the parent
}

/**
 * Checks if a node should be excluded from calculations
 * Rule: Exclude "Apple ASIN" dimension node and its children when "Beats" brand is selected
 */
function shouldExcludeFromCalculation(
  nodeKey: string,
  selectedKeys: Set<string>
): boolean {
  // Check if this node is "Apple ASIN" or under it
  const isAppleASINBranch = nodeKey.includes('Apple%20ASIN');
  
  if (!isAppleASINBranch) {
    return false; // Not Apple ASIN branch, don't exclude
  }
  
  // Check if any selected node is under "Beats" brand
  // Pattern match: the key should contain both "Brand" and "Beats" (URL encoded)
  const hasBeatsSelection = Array.from(selectedKeys).some(key => {
    return key.includes('Brand') && key.includes('Beats');
  });
  
  return hasBeatsSelection; // Exclude Apple ASIN branch if Beats is selected
}

/**
 * Recursively traverse the tree and collect information about selected nodes
 */
function collectNodeInfo(
  node: TreeNodeData,
  parentKey: string,
  selectedKeys: Set<string>,
  parentValue: number | null,
  parentContribution: number,
  result: Map<string, NodeInfo>
): void {
  const key = generateNodeKey(node.name, parentKey);

  // Skip this node and its children if it should be excluded from calculations
  if (shouldExcludeFromCalculation(key, selectedKeys)) {
    return;
  }

  // If this node is selected, store its information
  if (selectedKeys.has(key) && node.value !== undefined) {
    result.set(key, {
      node,
      key,
      parentValue,
      parentContribution,
    });
  }

  // Recursively process children
  if (node.children && node.children.length > 0) {
    const currentValue = node.value ?? parentValue;
    
    // Calculate this node's contribution to pass down
    let currentContribution = parentContribution;
    if (node.value !== undefined && parentValue !== null && parentValue !== 0) {
      const pctOfParent = (node.value / parentValue) * 100;
      currentContribution = (pctOfParent / 100) * parentContribution;
    }

    node.children.forEach((child) => {
      collectNodeInfo(
        child,
        key,
        selectedKeys,
        currentValue,
        currentContribution,
        result
      );
    });
  }
}

/**
 * Calculate metrics for a single node
 */
function calculateNodeMetrics(
  nodeInfo: NodeInfo,
  rootValue: number
): NodeMetrics {
  const { node, parentValue, parentContribution } = nodeInfo;
  const value = node.value ?? 0;
  const absValue = Math.abs(value);

  // Calculate percentage of parent
  let pctOfParent = 0;
  let absPctOfParent = 0;
  if (parentValue !== null && parentValue !== 0) {
    pctOfParent = (value / parentValue) * 100;
    absPctOfParent = Math.abs(pctOfParent);
  }

  // Calculate percentage of root using chain multiplication
  let pctOfRoot = 0;
  let absPctOfRoot = 0;
  if (rootValue !== 0) {
    // If this node has a parent, use chain multiplication
    if (parentValue !== null && parentValue !== 0) {
      pctOfRoot = (pctOfParent / 100) * parentContribution;
    } else {
      // If no parent (root node itself), calculate directly
      pctOfRoot = (value / rootValue) * 100;
    }
    absPctOfRoot = Math.abs(pctOfRoot);
  }

  const isPositiveDriver = value > 0;
  const sign: '+' | '-' = value >= 0 ? '+' : '-';

  return {
    value,
    abs_value: absValue,
    pct_of_parent: pctOfParent,
    abs_pct_of_parent: absPctOfParent,
    pct_of_root: pctOfRoot,
    abs_pct_of_root: absPctOfRoot,
    is_positive_driver: isPositiveDriver,
    sign,
  };
}

/**
 * Main function to calculate metrics for all selected nodes
 * 
 * @param rootData - The original tree data from the root
 * @param selectedNodeKeys - Set of keys for selected nodes
 * @returns Map of node key to calculated metrics
 */
export function calculateMetricsForSelectedNodes(
  rootData: TreeNodeData | null,
  selectedNodeKeys: Set<string>
): Map<string, NodeMetrics> {
  const metricsMap = new Map<string, NodeMetrics>();

  if (!rootData || selectedNodeKeys.size === 0) {
    return metricsMap;
  }

  const rootValue = rootData.value ?? 0;
  if (rootValue === 0) {
    return metricsMap;
  }

  // Collect information about all selected nodes
  const nodeInfoMap = new Map<string, NodeInfo>();
  const rootKey = generateNodeKey(rootData.name);
  
  // Start traversal from root with 100% contribution
  collectNodeInfo(
    rootData,
    '',
    selectedNodeKeys,
    null, // Root has no parent
    100, // Root contributes 100% to itself
    nodeInfoMap
  );

  // Calculate metrics for each selected node
  nodeInfoMap.forEach((nodeInfo, key) => {
    const metrics = calculateNodeMetrics(nodeInfo, rootValue);
    metricsMap.set(key, metrics);
  });

  return metricsMap;
}

/**
 * Get the level-1 parent key (direct child of root) for a given node key
 * 
 * @param nodeKey - The node key to find level-1 parent for
 * @param rootKey - The root node key
 * @returns The level-1 parent key, or null if this is the root or a level-1 node itself
 */
function getLevel1ParentKey(nodeKey: string, rootKey: string): string | null {
  const parts = nodeKey.split(':');
  
  // If this is the root node itself, return null
  if (nodeKey === rootKey) {
    return null;
  }
  
  // If this has only 2 parts (root:level1), it IS a level-1 node
  if (parts.length === 2) {
    return nodeKey;
  }
  
  // Otherwise, return root:level1 (first two parts)
  return `${parts[0]}:${parts[1]}`;
}

/**
 * Get the level-2 parent key (grandchild of root) for a given node key
 * 
 * @param nodeKey - The node key to find level-2 parent for
 * @param rootKey - The root node key
 * @returns The level-2 parent key, or null if this is the root, level-1, or level-2 node itself
 */
function getLevel2ParentKey(nodeKey: string, rootKey: string): string | null {
  const parts = nodeKey.split(':');
  
  // If this is the root node itself, return null
  if (nodeKey === rootKey) {
    return null;
  }
  
  // If this has only 2 parts (root:level1), it's a level-1 node
  if (parts.length === 2) {
    return null;
  }
  
  // If this has 3 parts (root:level1:level2), it IS a level-2 node
  if (parts.length === 3) {
    return nodeKey;
  }
  
  // Otherwise, return root:level1:level2 (first three parts)
  return `${parts[0]}:${parts[1]}:${parts[2]}`;
}

/**
 * Find a node by its key in the tree
 */
function findNodeByKey(node: TreeNodeData, targetKey: string, parentKey: string = ''): TreeNodeData | null {
  const currentKey = generateNodeKey(node.name, parentKey);
  
  if (currentKey === targetKey) {
    return node;
  }
  
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeByKey(child, targetKey, currentKey);
      if (found) {
        return found;
      }
    }
  }
  
  return null;
}

/**
 * Detects if all level-1 children have values (simple value tree like Rate & Mix)
 * or if some are dimension nodes (multi-branch tree like Sourcing Channel)
 * 
 * @param rootData - The root node
 * @param selectedKeys - Set of selected node keys (used to determine exclusions)
 * @returns true if all level-1 nodes are value nodes, false if any are dimension nodes
 */
function isSimpleValueTree(rootData: TreeNodeData, selectedKeys: Set<string>): boolean {
  if (!rootData.children || rootData.children.length === 0) {
    return true;
  }
  
  const rootKey = generateNodeKey(rootData.name);
  
  // Check if all immediate children (level-1 nodes) have values
  // Exclude nodes that should be filtered out from calculations
  return rootData.children.every(child => {
    const childKey = generateNodeKey(child.name, rootKey);
    
    // Skip excluded nodes in structure detection - they shouldn't affect the tree type
    if (shouldExcludeFromCalculation(childKey, selectedKeys)) {
      return true; // Don't let excluded nodes affect the detection
    }
    
    return child.value !== undefined;
  });
}

/**
 * Groups selected node keys by their level-1 ancestor branch
 * 
 * @param rootData - The root node
 * @param selectedKeys - Set of selected node keys
 * @returns Map of level-1 key to array of selected node keys under that branch
 */
function groupSelectionsByLevel1Ancestor(
  rootData: TreeNodeData,
  selectedKeys: Set<string>
): Map<string, string[]> {
  const rootKey = generateNodeKey(rootData.name);
  const branchGroups = new Map<string, string[]>();
  
  selectedKeys.forEach((key) => {
    // Skip the root node itself
    if (key === rootKey) {
      return;
    }
    
    // Find the level-1 ancestor for this selected node
    const level1Key = getLevel1ParentKey(key, rootKey);
    if (level1Key) {
      if (!branchGroups.has(level1Key)) {
        branchGroups.set(level1Key, []);
      }
      branchGroups.get(level1Key)!.push(key);
    }
  });
  
  return branchGroups;
}

/**
 * Gets unique level-2 node keys that are affected by selections
 * For multi-branch trees, any selection rolls up to its level-2 ancestor
 * 
 * @param rootData - The root node
 * @param selectedKeys - Set of selected node keys
 * @returns Set of unique level-2 node keys
 */
function getAffectedLevel2Nodes(
  rootData: TreeNodeData,
  selectedKeys: Set<string>
): Set<string> {
  const rootKey = generateNodeKey(rootData.name);
  const level2Keys = new Set<string>();
  
  selectedKeys.forEach((key) => {
    // Skip the root node itself
    if (key === rootKey) {
      return;
    }
    
    // Roll up to level-2 ancestor
    const level2Key = getLevel2ParentKey(key, rootKey);
    if (level2Key) {
      level2Keys.add(level2Key);
    }
  });
  
  return level2Keys;
}

/**
 * Calculates sum of unique level-1 node values (for simple value trees like Rate & Mix)
 * 
 * @param rootData - The root node
 * @param level1Keys - Set of unique level-1 node keys that have selections
 * @returns Sum of level-1 node values
 */
function calculateSumOfLevel1Nodes(
  rootData: TreeNodeData,
  level1Keys: Set<string>
): number {
  let total = 0;
  
  level1Keys.forEach((level1Key) => {
    // Find the level-1 node in the tree and get its value
    const node = findNodeByKey(rootData, level1Key);
    if (node && node.value !== undefined) {
      total += node.value;
    }
  });
  
  return total;
}

/**
 * Calculates the sum of selected nodes within a specific branch,
 * including the branch root itself if selected
 * 
 * @param rootData - The root node
 * @param branchKey - The level-1 branch key
 * @param selectedKeysInBranch - Array of selected node keys under this branch
 * @param metricsMap - Map of node metrics
 * @returns Sum of all selected nodes in this branch
 */
function calculateBranchSum(
  rootData: TreeNodeData,
  branchKey: string,
  selectedKeysInBranch: string[],
  metricsMap: Map<string, NodeMetrics>
): number {
  let branchSum = 0;
  
  // Sum all selected nodes under this branch
  selectedKeysInBranch.forEach((key) => {
    const metrics = metricsMap.get(key);
    if (metrics) {
      branchSum += metrics.value;
    }
  });
  
  // Also check if the branch root itself is selected
  const branchNode = findNodeByKey(rootData, branchKey);
  if (branchNode && branchNode.value !== undefined && branchNode.selected) {
    // Branch root is selected, its value should be included
    const branchMetrics = metricsMap.get(branchKey);
    if (branchMetrics) {
      branchSum += branchMetrics.value;
    }
  }
  
  return branchSum;
}

/**
 * Calculates branch sums for dimension trees: SUM within each level-1 branch, then MAX across branches
 * 
 * This implements the algorithm:
 * 1. Group level-2 nodes by their level-1 parent
 * 2. SUM all level-2 node values within each level-1 branch
 * 3. Return MAX of these branch sums (by absolute value)
 * 
 * @param rootData - The root node
 * @param level2Keys - Set of level-2 node keys affected by selections
 * @returns Maximum branch sum value (with sign preserved)
 */
function calculateMaxOfBranchSums(
  rootData: TreeNodeData,
  level2Keys: Set<string>
): number {
  if (level2Keys.size === 0) {
    return 0;
  }
  
  // Step 1: Group level-2 nodes by their level-1 parent
  const branchSums = new Map<string, number>();
  
  level2Keys.forEach((level2Key) => {
    // Get the level-1 parent for this level-2 node
    const parts = level2Key.split(':');
    if (parts.length >= 2) {
      const level1Key = `${parts[0]}:${parts[1]}`;
      
      // Get the value of this level-2 node
      const node = findNodeByKey(rootData, level2Key);
      if (node && node.value !== undefined) {
        // Add to the branch sum
        const currentSum = branchSums.get(level1Key) || 0;
        branchSums.set(level1Key, currentSum + node.value);
      }
    }
  });
  
  // Step 2: Find the MAX branch sum by absolute value
  let maxAbsValue = 0;
  let valueWithMaxAbs = 0;
  
  branchSums.forEach((branchSum) => {
    const absValue = Math.abs(branchSum);
    
    if (absValue > maxAbsValue) {
      maxAbsValue = absValue;
      valueWithMaxAbs = branchSum;
    }
  });
  
  return valueWithMaxAbs;
}

/**
 * Calculate the total variance from selected nodes.
 * 
 * Two modes:
 * 1. Simple Value Tree (e.g., Rate & Mix): All level-1 nodes have values
 *    - Algorithm: SUM of unique level-1 node values
 * 
 * 2. Dimension Trees (e.g., GV Velocity, FC Type/Sourcing Channel): Level-1 nodes are dimension nodes
 *    - Algorithm: Roll selections up to level-2 ancestors, then:
 *      a) SUM level-2 node values within each level-1 branch
 *      b) Take MAX of these branch sums (by absolute value)
 * 
 * @param rootData - The original tree data from the root
 * @param selectedKeys - Set of keys for selected nodes
 * @param metricsMap - Map of node metrics (unused in current implementation but kept for API compatibility)
 * @returns Total variance
 */
export function calculateTotalVariance(
  rootData: TreeNodeData | null,
  selectedKeys: Set<string>,
  metricsMap: Map<string, NodeMetrics>
): number {
  if (!rootData || selectedKeys.size === 0) {
    return 0;
  }

  const rootKey = generateNodeKey(rootData.name);
  
  // Detect tree structure type
  const isSimpleTree = isSimpleValueTree(rootData, selectedKeys);
  
  if (isSimpleTree) {
    // Mode 1: Simple Value Tree (Rate & Mix)
    // Collect unique level-1 parent keys, excluding nodes that should be filtered out
    const level1Keys = new Set<string>();
    
    selectedKeys.forEach((key) => {
      if (key === rootKey) {
        return;
      }
      
      // Skip excluded nodes from level-1 key collection
      if (shouldExcludeFromCalculation(key, selectedKeys)) {
        return;
      }
      
      const level1Key = getLevel1ParentKey(key, rootKey);
      if (level1Key) {
        // Also check if the level-1 key itself should be excluded
        if (!shouldExcludeFromCalculation(level1Key, selectedKeys)) {
          level1Keys.add(level1Key);
        }
      }
    });
    
    return calculateSumOfLevel1Nodes(rootData, level1Keys);
    
  } else {
    // Mode 2: Dimension Trees (single or multi-dimension)
    // Roll all selections up to level-2 ancestors
    const level2Keys = getAffectedLevel2Nodes(rootData, selectedKeys);
    
    // SUM within branches, MAX across branches
    return calculateMaxOfBranchSums(rootData, level2Keys);
  }
}

/**
 * Calculate what percentage the total selected variance is of the root
 * 
 * @param totalVariance - Sum of selected variances
 * @param rootValue - Root node value
 * @returns Percentage of root
 */
export function calculateTotalPctOfRoot(totalVariance: number, rootValue: number): number {
  if (rootValue === 0) {
    return 0;
  }
  return (totalVariance / rootValue) * 100;
}
